import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { creditsService } from '@/services/creditsService';

interface CreditsContextType {
  balance: number;
  loading: boolean;
  tier: 'free' | 'premium' | 'enterprise';
  refreshBalance: () => Promise<void>;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
};

interface CreditsProviderProps {
  children: React.ReactNode;
}

export const CreditsProvider: React.FC<CreditsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [tier, setTier] = useState<'free' | 'premium' | 'enterprise'>('free');
  const [loading, setLoading] = useState(true);

  const refreshBalance = async () => {
    if (!user) {
      setBalance(0);
      setTier('free');
      setLoading(false);
      return;
    }

    try {
      const [userBalance, userTier] = await Promise.all([
        creditsService.getBalance(user.uid),
        creditsService.getUserTier(user.uid),
      ]);
      setBalance(userBalance);
      setTier(userTier);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      // Set defaults if credits document doesn't exist yet
      // It will be created on next login via authService
      setBalance(0);
      setTier('free');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshBalance();
    
    if (user) {
      const interval = setInterval(refreshBalance, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const value: CreditsContextType = {
    balance,
    loading,
    tier,
    refreshBalance,
  };

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
};

